// MFG display format: dates MM-DD-YYYY, timestamps MM-DD-YYYY hh:mm:ss AM/PM in the viewer's device time zone.
const pad = (n) => String(n).padStart(2, "0");
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Date-only ISO strings ("2026-07-20") stay on their calendar day (no TZ shift); anything else parses as an instant. */
export const toDate = (v) => {
  if (v == null || v === "") return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v === "number") return new Date(v < 1e12 ? v * 1000 : v);
  const s = String(v).trim();
  if (DATE_ONLY.test(s)) { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); }
  const d = new Date(s);
  return isNaN(d) ? null : d;
};

export const fmtDate = (v, fallback = "—") => {
  const d = toDate(v);
  return d ? `${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${d.getFullYear()}` : fallback;
};

export const fmtTime = (v, fallback = "—", { seconds = true } = {}) => {
  const d = toDate(v);
  if (!d) return fallback;
  const h = d.getHours();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${pad(h12)}:${pad(d.getMinutes())}${seconds ? `:${pad(d.getSeconds())}` : ""} ${h < 12 ? "AM" : "PM"}`;
};

export const fmtDateTime = (v, fallback = "—", opts) => {
  const d = toDate(v);
  return d ? `${fmtDate(d)} ${fmtTime(d, fallback, opts)}` : fallback;
};

/** Same format but pinned to a named zone (used for Eastern-time legal screens). */
export const fmtInZone = (v, timeZone, { dateOnly = false, seconds = true } = {}) => {
  const d = toDate(v);
  if (!d) return "—";
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })
    .formatToParts(d).map((p) => [p.type, p.value]));
  const date = `${parts.month}-${parts.day}-${parts.year}`;
  if (dateOnly) return date;
  return `${date} ${parts.hour}:${parts.minute}${seconds ? `:${parts.second}` : ""} ${parts.dayPeriod}`;
};

/** For native <input type="date"> — shows the picked value the MFG way next to the picker. */
export const fmtDateHint = (v) => (v ? fmtDate(v, "") : "");

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
/** Rewrites dates embedded in stored free text (notifications, period labels) to MM-DD-YYYY: ISO 'YYYY-MM-DD' and 'Aug 23, 2026' / 'Aug 23' (year inferred from a later 'Mon DD, YYYY' in the same text or the current year). */
export const normalizeDatesInText = (text) => {
  if (typeof text !== "string" || !text) return text;
  let out = text.replace(/\b(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?\b/g, (m, y, mo, d) => (m.length > 10 ? fmtDateTime(m) : `${mo}-${d}-${y}`));
  const yearHint = (out.match(/\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\.? \d{1,2},? (\d{4})\b/) || [])[1] || String(new Date().getFullYear());
  out = out.replace(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\.? (\d{1,2})(?:,? (\d{4}))?\b(?! (?:AM|PM))/g, (m, mon, d, y) => `${pad(MONTHS[mon.toLowerCase()])}-${pad(d)}-${y || yearHint}`);
  return out;
};
